/**
 * Customer subscription controls (ACC-021 to ACC-024, PAY-020, PAY-021): pause, skip, move, cancel
 * and restart, plus the payload the account billing page reads. Type aliases so the Worker's
 * `json()` accepts the body.
 */
import { z } from "zod";
import type { SubscriptionState } from "./machines.ts";
import { addUtcDays, addUtcMonths, SCRIPT_VALIDITY_DAYS } from "./scripts.ts";

/** ACC-021: the next ship date may move forward by at most this many days. */
export const MOVE_SHIP_MAX_DAYS = 30;

export const MOVE_SHIP_CHOICES = [7, 14, 30] as const;

export type MoveShipDays = (typeof MOVE_SHIP_CHOICES)[number];

/** A move of 1 to 30 days, or null when the field is not a whole number in that range. */
export function moveDays(input: string): number | null {
  if (!/^[0-9]+$/.test(input)) return null;
  const days = Number(input);

  return days >= 1 && days <= MOVE_SHIP_MAX_DAYS ? days : null;
}

function dayOf(value: string): string {
  return value.slice(0, 10);
}

/** The current next-ship day, or one month from today when none is stored yet. */
export function currentShipOn(nextShipOn: string | null, today: string): string {
  return nextShipOn === null ? addUtcMonths(dayOf(today), 1) : dayOf(nextShipOn);
}

/** Skip the next pouch: the ship date moves forward one month. */
export function skipShipOn(nextShipOn: string | null, today: string): string {
  return addUtcMonths(currentShipOn(nextShipOn, today), 1);
}

/** Move the next ship date forward by `days` (already clamped by `moveDays`). */
export function moveShipOn(nextShipOn: string | null, today: string, days: number): string {
  return addUtcDays(currentShipOn(nextShipOn, today), days);
}

/** Pause of `months` also pushes the next ship date by that many months. */
export function pauseShipOn(nextShipOn: string | null, today: string, months: number): string {
  return addUtcMonths(currentShipOn(nextShipOn, today), months);
}

/**
 * ACC-023: labs older than the script window (180 days) need a new physician review before
 * shipping can start again. No labs at all also needs a review.
 */
export function labsNeedReview(latestLabDay: string | null, today: string): boolean {
  if (latestLabDay === null) return true;

  return addUtcDays(dayOf(latestLabDay), SCRIPT_VALIDITY_DAYS) < dayOf(today);
}

/** Unix seconds at 00:00 UTC on a YYYY-MM-DD day, for Stripe billing anchors. */
export function unixFromDay(day: string): number {
  return Math.floor(new Date(`${dayOf(day)}T00:00:00Z`).getTime() / 1000);
}

export type AccountSubscription = {
  readonly status: SubscriptionState | "none";
  readonly nextShipOn: string | null;
  readonly pausedUntil: string | null;
  readonly cancelledAt: string | null;
  /** Set when cancel is scheduled at period end and Stripe has not ended it yet. */
  readonly endingOn: string | null;
  readonly needsReview: boolean;
};

export type AccountPortal = {
  readonly url: string;
};

const Status = z.enum(["active", "paused", "past_due", "cancelled", "none"]);

const Subscription = z.object({
  status: Status,
  nextShipOn: z.string().nullable(),
  pausedUntil: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  endingOn: z.string().nullable(),
  needsReview: z.boolean(),
});

const Portal = z.object({ url: z.string().min(1) });

/** The account billing payload from a JSON body, or null when it is not one. */
export function readAccountSubscription(text: string): AccountSubscription | null {
  try {
    const parsed = Subscription.safeParse(JSON.parse(text));

    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** A Stripe portal (or fake) URL from a JSON body, or null when it is not one. */
export function readAccountPortal(text: string): AccountPortal | null {
  try {
    const parsed = Portal.safeParse(JSON.parse(text));

    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export type StripeSubscriptionFacts = {
  readonly status: string | null;
  readonly cancelAtPeriodEnd: boolean;
  readonly periodEndOn: string | null;
  readonly pauseResumesOn: string | null;
};

export type StripeMappedSubscription = {
  readonly status: SubscriptionState;
  readonly pausedUntil: string | null;
  readonly cancelledAt: string | null;
  readonly nextShipOn: string | null;
};

/**
 * Local subscription fields from a Stripe subscription object (PAY-021). Unknown Stripe statuses
 * stay active so a new Stripe state cannot invent a row we cannot store.
 */
export function subscriptionFromStripe(facts: StripeSubscriptionFacts): StripeMappedSubscription {
  const ended =
    facts.status === "canceled" ||
    facts.status === "unpaid" ||
    facts.status === "incomplete_expired";

  if (ended) {
    return {
      status: "cancelled",
      pausedUntil: null,
      cancelledAt: facts.periodEndOn,
      nextShipOn: facts.periodEndOn,
    };
  }

  if (facts.status === "paused" || facts.pauseResumesOn !== null) {
    return {
      status: "paused",
      pausedUntil: facts.pauseResumesOn,
      cancelledAt: null,
      nextShipOn: facts.pauseResumesOn ?? facts.periodEndOn,
    };
  }

  if (facts.status === "past_due") {
    return {
      status: "past_due",
      pausedUntil: null,
      cancelledAt: facts.cancelAtPeriodEnd ? facts.periodEndOn : null,
      nextShipOn: facts.periodEndOn,
    };
  }

  return {
    status: "active",
    pausedUntil: null,
    cancelledAt: facts.cancelAtPeriodEnd ? facts.periodEndOn : null,
    nextShipOn: facts.periodEndOn,
  };
}
